import React from 'react';

import { Table, Tag } from 'antd';

import { SimAlgorithmResult } from './scenario';
import * as S from './styled';

export const ALGORITHM_TITLES: Record<SimAlgorithmResult['algorithm'], string> = {
  EQUALIZE: 'Geral',
  PHASE: 'Por Fases',
};

export const ALGORITHM_RULES: Record<SimAlgorithmResult['algorithm'], string> = {
  EQUALIZE: 'O limite vale para a soma de todos os conectores, em qualquer fase.',
  PHASE: 'O limite vale para cada fase: cada uma das três pode chegar ao limite.',
};

const formatNumber = (value: number, digits = 1) =>
  value.toLocaleString('pt-BR', { minimumFractionDigits: digits, maximumFractionDigits: digits });

type BarProps = {
  label: string;
  connectorsA: number;
  externalA: number;
  securityA: number;
  limitA: number;
  over: boolean;
};

/** Uso contra o limite: conectores, carga externa e reserva empilhados; o traço preto é o limite. */
const UsageBar = ({ label, connectorsA, externalA, securityA, limitA, over }: BarProps) => {
  const total = connectorsA + externalA + securityA;
  const scale = Math.max(limitA, total, 1);
  const percent = (value: number) => (value / scale) * 100;

  return (
    <S.BarRow>
      <strong>{label}</strong>
      <S.BarTrack aria-label={`${label}: ${formatNumber(total)} A de ${formatNumber(limitA, 0)} A`}>
        <S.ConnectorsSegment $color='' $width={percent(connectorsA)} />
        <S.BarSegment $color={S.EXTERNAL_COLOR} $width={percent(externalA)} />
        <S.BarSegment $color={S.RESERVE_COLOR} $width={percent(securityA)} />
        <S.LimitMark $left={percent(limitA)} />
      </S.BarTrack>
      <span style={{ color: over ? S.OVER_COLOR : undefined, fontWeight: over ? 700 : 400 }}>
        {formatNumber(total)} / {formatNumber(limitA, 0)} A
      </span>
    </S.BarRow>
  );
};

type AlgorithmResultProps = {
  result: SimAlgorithmResult;
  securityA: number;
  limitA: number;
};

export const AlgorithmResult = ({ result, securityA, limitA }: AlgorithmResultProps) => {
  const perPhase = result.limit_rule === 'PER_PHASE';
  const charging = result.connectors.filter((connector) => connector.charging);
  const maxExternalA = Math.max(0, ...result.phases.map((phase) => phase.external_a));

  return (
    <S.Card data-testid={`simulation-${result.algorithm}`}>
      <S.ResultHeader>
        <S.Gauge $over={result.over_limit}>
          <strong>{formatNumber(result.total_power_kw)}</strong>
          <span>kW</span>
          <span>{formatNumber(result.total_current_a)} A</span>
        </S.Gauge>
        <div style={{ flex: '1 1 200px', minWidth: 0 }}>
          <S.CardTitle>{ALGORITHM_TITLES[result.algorithm]}</S.CardTitle>
          <S.CardText style={{ marginBottom: 8 }}>{ALGORITHM_RULES[result.algorithm]}</S.CardText>
          {result.over_limit ? (
            <Tag color='red'>Passa do limite</Tag>
          ) : (
            <Tag color='green'>Dentro do limite</Tag>
          )}
          {result.meter_fail_safe && <Tag color='orange'>Medidor sem leitura: todos em 6 A</Tag>}
        </div>
      </S.ResultHeader>

      {perPhase ? (
        result.phases.map((phase) => (
          <UsageBar
            key={phase.phase}
            label={`Fase ${phase.phase}`}
            connectorsA={phase.connectors_a}
            externalA={phase.external_a}
            securityA={securityA}
            limitA={limitA}
            over={Boolean(phase.over_limit)}
          />
        ))
      ) : (
        <>
          <UsageBar
            label='Soma'
            connectorsA={result.total_current_a}
            externalA={maxExternalA}
            securityA={securityA}
            limitA={limitA}
            over={result.over_limit}
          />
          <S.Hint style={{ marginTop: 6 }}>
            Por fase: {result.phases.map((phase) => `${phase.phase} ${formatNumber(phase.connectors_a)} A`).join(' · ')}
          </S.Hint>
        </>
      )}

      <S.Legend>
        <span><S.LegendPrimaryDot />Conectores</span>
        <span><S.LegendDot $color={S.EXTERNAL_COLOR} />{perPhase ? 'Carga externa' : 'Maior fase do medidor'}</span>
        <span><S.LegendDot $color={S.RESERVE_COLOR} />Reserva de {formatNumber(securityA, 0)} A</span>
      </S.Legend>

      <Table
        style={{ marginTop: 12 }}
        size='small'
        pagination={false}
        rowKey={(row) => `${row.connector_id || row.charge_box_id || row.code}-${row.seq}`}
        dataSource={charging}
        locale={{ emptyText: 'Nenhum carro carregando' }}
        columns={[
          {
            title: 'Estação',
            render: (_, row) => `${row.code} · ${row.seq}`,
          },
          { title: 'Fases', dataIndex: 'phases' },
          {
            title: 'Corrente',
            align: 'right',
            render: (_, row) => `${formatNumber(row.current_a)} A`,
          },
          {
            title: 'Potência',
            align: 'right',
            render: (_, row) => `${formatNumber(row.power_kw)} kW`,
          },
        ]}
      />
    </S.Card>
  );
};

/** Quanto o Por Fases entrega a mais (ou a menos) que o Geral no mesmo cenário. */
export const compareAlgorithms = (results: SimAlgorithmResult[]) => {
  const general = results.find((result) => result.algorithm === 'EQUALIZE');
  const phase = results.find((result) => result.algorithm === 'PHASE');

  if (!general || !phase) {
    return null;
  }

  const diffKw = Math.round((phase.total_power_kw - general.total_power_kw) * 10) / 10;
  const percent = general.total_power_kw > 0 ? Math.round((diffKw / general.total_power_kw) * 100) : null;

  return { diffKw, percent };
};

export const CompareBanner = ({ results }: { results: SimAlgorithmResult[] }) => {
  const comparison = compareAlgorithms(results);

  if (!comparison) {
    return null;
  }

  const { diffKw, percent } = comparison;
  let text = 'Os dois algoritmos entregam a mesma potência neste cenário.';

  if (diffKw > 0) {
    text = `Por Fases entrega ${formatNumber(diffKw)} kW a mais que o Geral${percent !== null ? ` (+${percent}%)` : ''}.`;
  } else if (diffKw < 0) {
    text = `Geral entrega ${formatNumber(-diffKw)} kW a mais que o Por Fases neste cenário.`;
  }

  return (
    <S.Compare id='sim-compare' $gain={diffKw > 0}>
      {text}
    </S.Compare>
  );
};
